import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, MenuItem, FormControlLabel, Switch, Box, Drawer, Typography } from '@mui/material';
import { SCUIButton } from '../../../../atomic/atoms';
import {
    StudioAddressBarcode,
    StudioAddressBarcodeRequest,
    ProductStatusOption,
} from '../../../../types/StudioAddressBarcode';
import { StudioAddressBarcodeCommand } from '../../../../services/command/studioService/StudioAddressBarcodeCommand';
import Swal from 'sweetalert2';

interface StudioAddressBarcodeDrawerProps {
    open: boolean;
    onClose: () => void;
    addressBarcode: StudioAddressBarcode | null;
    onSaveSuccess: () => void;
    addressBarcodeStatuses: ProductStatusOption[];
}

export const StudioAddressBarcodeDrawer: React.FC<StudioAddressBarcodeDrawerProps> = ({
    open,
    onClose,
    addressBarcode,
    onSaveSuccess,
    addressBarcodeStatuses,
}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        barcode: '',
        address: '',
        shotStatus: '' as unknown as number,
        isActive: true,
    });
    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        if (open) {
            if (addressBarcode) {
                setFormData({
                    barcode: addressBarcode.addressBarcode,
                    address: addressBarcode.addressDefinition,
                    shotStatus: addressBarcode.shotStatusId,
                    isActive: addressBarcode.active,
                });
            } else {
                setFormData({
                    barcode: '',
                    address: '',
                    shotStatus: '' as unknown as number,
                    isActive: true,
                });
            }
            setErrors({});
        }
    }, [open, addressBarcode]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev: any) => ({ ...prev, [field]: '' }));
        }
    };

    const validate = () => {
        const newErrors: any = {};
        if (!formData.address) newErrors.address = t('general.required');
        if (!formData.barcode) newErrors.barcode = t('general.required');
        if (!formData.shotStatus) newErrors.shotStatus = t('general.required');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            const request: StudioAddressBarcodeRequest = {
                addressDefinition: formData.address,
                addressBarcode: formData.barcode,
                shotStatusId: formData.shotStatus,
                active: formData.isActive,
            };

            if (addressBarcode) {
                request.addressBarcodeId = addressBarcode.addressBarcodeId || addressBarcode.id;
                await StudioAddressBarcodeCommand.UpdateStudioAddressBarcode(request);
            } else {
                await StudioAddressBarcodeCommand.CreateStudioAddressBarcode(request);
            }

            Swal.fire({
                icon: 'success',
                title: t('general.success'),
                text: t('studioAddressBarcode.saveSuccess'),
                confirmButtonText: t('general.ok'),
            });
            onSaveSuccess();
        } catch (error) {
            console.error('Error saving address barcode:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studioAddressBarcode.errorOccured'),
                confirmButtonText: t('general.ok'),
            });
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 500, p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {addressBarcode ? t('studioAddressBarcode.editTitle') : t('studioAddressBarcode.addTitle')}
                </Typography>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
                >
                    <TextField
                        fullWidth
                        id="barcode"
                        name="barcode"
                        label={t('studioAddressBarcode.barcode')}
                        value={formData.barcode}
                        onChange={(e) => handleChange('barcode', e.target.value)}
                        error={Boolean(errors.barcode)}
                        helperText={errors.barcode}
                    />
                    <TextField
                        fullWidth
                        id="address"
                        name="address"
                        label={t('studioAddressBarcode.address')}
                        value={formData.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        error={Boolean(errors.address)}
                        helperText={errors.address}
                    />
                    <TextField
                        select
                        fullWidth
                        id="shotStatus"
                        name="shotStatus"
                        label={t('studioAddressBarcode.shotStatus')}
                        value={formData.shotStatus}
                        onChange={(e) => handleChange('shotStatus', e.target.value)}
                        error={Boolean(errors.shotStatus)}
                        helperText={errors.shotStatus}
                    >
                        {addressBarcodeStatuses.map((option) => (
                            <MenuItem key={option.shotStatusId} value={option.shotStatusId}>
                                {option.description || (option.shotStatusKey ? t(option.shotStatusKey) : option.name)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                name="isActive"
                                color="primary"
                            />
                        }
                        label={t('general.active')}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <SCUIButton
                            text={t('general.cancel')}
                            type="secondary"
                            onClick={onClose}
                            style={{ marginRight: 8 }}
                            buttonType="button"
                            sizeType={'fixed'}
                            size={'m'}
                        />
                        <SCUIButton
                            text={t('general.save')}
                            type="primary"
                            buttonType="submit"
                            sizeType={'fixed'}
                            size={'m'}
                        />
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};
